import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useHistory } from 'react-router-dom';
import Select from 'react-select';
import { Form, FormGroup, Label } from 'reactstrap';
import { Button, InputField } from 'src/components';
import { setFromErrors } from 'src/helpers';
import { useGetFcpsCourseListsForLogbookEntryMutation, useLazyGetFormByLogbookTypeAndSubjectQuery } from 'src/rtk/features/setup/logBookEntrySetupDetails/logBookEntrySetupDetailsApi';
import { useCreateTargetSetupMutation } from 'src/rtk/features/setup/logBookEntrySetupMaster/logBookEntrySetupMasterApi';
import { useGetSubjectInfosQuery } from 'src/rtk/features/setup/subjectInfo/subjectInfoApi';
import * as Yup from 'yup';

const initialValues: TargetSetup = {
  id: '',
  logbook_entry_setup_master_id: '',
  year: '',
  min_value: '',
};

interface QueryParams {
  id?: number;
  course_type?: string;
  subject_id?: string;
  main_training?: string;
}

export const TargetSetupForAcademicActivities = () => {
  const [createTargetSetup, { isSuccess, isError, error }] = useCreateTargetSetupMutation();
  const [getForms, { data: formSetups, isSuccess: isFormSetupsSuccess, isLoading: isFormSetupsLoading, isFetching: isFormSetupsFetching }] =
    useLazyGetFormByLogbookTypeAndSubjectQuery();

  const [showStatus, setShowStatus] = useState<boolean>(true);
  const [subject, setSubject] = useState<SubjectInfo>(
    {} as SubjectInfo
  );
  const [queryParams, setQueryParams] = useState<QueryParams>(
    {} as QueryParams,
  );
  const [selectedSubject, setSelectedSubject] = useState<any>('');

  const [mainTrainings, setMainTrainings] = useState<FcpsCourseList[]>([]);
  const [forms, setForms] = useState<LogbookEntrySetupMaster[]>([]);
  const [selectedForm, setSelectedForm] = useState<LogbookEntrySetupMaster | null>(null);
  const [selectedSystemTypeObject, setSelectedSystemTypeObject] = useState<LogbookEntrySetupMaster | null>(null);
  const [selectedTopicTypeObject, setSelectedTopicTypeObject] = useState<LogbookEntrySetupMaster | null>(null);
  const [coreAdvanceOptions, setCoreAdvanceOptions] = useState<SelectOption[]>([]);

  // New state for entry type and system type
  const [systemTypes, setSystemTypes] = useState<any[]>([]);
  const [selectedSystemType, setSelectedSystemType] = useState<string>('');

  const [mainTraining, setMainTraining] = useState<FcpsCourseList>(
    {} as FcpsCourseList,
  );

  const { data: getSubjectInfolist } = useGetSubjectInfosQuery();
  const subjects = getSubjectInfolist?.data;

  let formOptions: SelectOption[] =
    forms?.map((e: LogbookEntrySetupMaster) => {
      return {
        label: e.particulars!,
        value: e.id!,
      };
    }) ?? [];


  // system type options from selected form's entry_master_data
  const systemTypeOptions: SelectOption[] =
    systemTypes?.map((caseType: any) => ({
      label: caseType.particulars, // Adjust based on your data structure
      value: caseType.id,
    })) || [];


  const [createFcpsCourse, { data: fcpsCourseListData, isSuccess: isFcpsCourseListSuccess, isError: isFcpsCourseListError, error: fcpsCourseListError }] =
    useGetFcpsCourseListsForLogbookEntryMutation();

  const history = useHistory();

  const err: CustomError = error as CustomError;
  const { handleSubmit, handleChange, setFieldValue, getFieldProps, values, touched, errors, resetForm, setErrors } =
    useFormik({
      initialValues,
      validationSchema: Yup.object({
        logbook_entry_setup_master_id: Yup.string().required('Please select the logbook master'),
      }),

      onSubmit: (values) => {
        if (selectedSystemTypeObject?.log_entry_target[0]?.min_value) {
          values.id = selectedSystemTypeObject?.log_entry_target[0]?.id;
        }
        createTargetSetup({ ...values });
        console.log(values);
      },
    });

  useEffect(() => {
    if (isSuccess) {
      resetForm();
      toast.success('Academic Target Setup Added Successfully');
    }
    if (isError) {
      toast.error(err?.message || 'Something went wrong');
      setFromErrors(err.data, setErrors);
    }
  }, [isSuccess, isError]);

  let subjectOptions: SelectOption[] =
    subjects?.map((subject) => ({
      value: subject.id!,
      label: subject.subject_name,
    })) || [];

  let mainTrainingOptions: SelectOption[] =
    mainTrainings?.map((mainTraining) => ({
      value: mainTraining.id!,
      label: mainTraining.name!,
    })) || [];

  useEffect(() => {
    if (subject?.id) {
      createFcpsCourse(subject?.id!);
    }
  }, [subject]);

  useEffect(() => {
    if (isFcpsCourseListSuccess && fcpsCourseListData?.success) {
      const mainTrainings: FcpsCourseList[] = fcpsCourseListData.data;
      setMainTrainings(mainTrainings);
    } else {
      setMainTrainings([]);
    }
  }, [isFcpsCourseListSuccess, fcpsCourseListData?.success]);

  const handleMainTrainingOption = (e: any) => {
    const { options, selectedIndex, value } = e.target;
    const mainTraining = mainTrainings?.find(
      (mainTraining) => mainTraining.id === value,
    );
    setMainTraining(mainTraining!);
    const newQueryParams: QueryParams = { ...queryParams };
    newQueryParams.main_training = mainTraining?.id;
    newQueryParams.course_type = '';
    setQueryParams(newQueryParams);
    setForms([]);

    if (value === '') {
      setCoreAdvanceOptions([]);
    } else {
      setCoreAdvanceOptions([
        { label: 'Core', value: 'core' },
        { label: 'Specialty', value: 'adv' },
      ]);
    }
  };

  const handleCoreAdvance = (e: any) => {
    const { options, selectedIndex, value } = e.target;
    values.year = value;
    const newQueryParams: QueryParams = { ...queryParams };
    newQueryParams.course_type = value;
    setQueryParams(newQueryParams);

    if (value === '') {
      setForms([]);
    } else {
      setForms(formSetups?.data!);
    }
  };


  useEffect(() => {
    const subject = subjects?.find((subject) => subject.id === selectedSubject?.value);
    setSubject(subject!);

    const newQueryParams: QueryParams = { ...queryParams };
    newQueryParams.subject_id = subject?.id;
    newQueryParams.main_training = '';
    newQueryParams.course_type = '';
    setQueryParams(newQueryParams);
    setCoreAdvanceOptions([]);
    setForms([]);

    setMainTraining({ id: '' } as FcpsCourseList);

    if (selectedSubject?.value === '') {
      setMainTrainings([]);
    }
  }, [selectedSubject]);

  useEffect(() => {
    if (
      queryParams.subject_id &&
      queryParams.main_training &&
      queryParams.course_type
    ) {
      const updatedParams = { ...queryParams, id: 2 };
      getForms(updatedParams);
    }
  }, [queryParams]);

  useEffect(() => {
    if (formSetups && isFormSetupsSuccess) {
      setForms(formSetups?.data);
    }
  }, [formSetups, isFormSetupsSuccess]);

  const handleFormSelect = (e: any) => {
    const { options, selectedIndex, value } = e.target;
    const form = forms.find((form) => form.id === value);
    setSelectedForm(form || null);

    setSelectedSystemType('');

    console.log(form);
  };

  useEffect(() => {
    if (selectedForm && selectedForm.entry_setup_master) {
      setSystemTypes(selectedForm.entry_setup_master);
    } else {
      setSystemTypes([]);
    }
  }, [selectedForm]);

  const handleSystemTypeSelect = (e: any) => {
    const { value } = e.target;
    const systemType = systemTypes.find((systemType) => systemType.id === value);
    setSelectedSystemTypeObject(systemType || null);

    setSelectedSystemType(value);
  };

  useEffect(() => {
    if (selectedSystemTypeObject) {
      setFieldValue('logbook_entry_setup_master_id', selectedSystemTypeObject?.id);
      console.log(selectedSystemTypeObject?.log_entry_target[0])
      if (selectedSystemTypeObject?.log_entry_target) {
        setFieldValue('min_value', selectedSystemTypeObject?.log_entry_target[0]?.min_value);
      }
    }
  }, [selectedSystemTypeObject]);


  return (
    <>
      <div className="row align-items-center justify-content-center">
        <Form
          className="custom-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <div className="row">
            <div className="col-lg-4 col-md-6 col-sm-12">
              <FormGroup className="mb-3">
                <Label className="form-label">Subject</Label>
                <Select
                  isClearable={true}
                  isSearchable={true}
                  value={selectedSubject}
                  onChange={setSelectedSubject}
                  options={subjectOptions}
                  placeholder="Select Subject"
                  name="subject"
                  id="subject"
                />
              </FormGroup>
            </div>

            <div className="col-lg-4 col-md-6 col-sm-12">
              <FormGroup className="mb-3">
                <Label className="form-label">Main Training</Label>
                <InputField
                  type="select"
                  name="mainTraining"
                  placeholder=""
                  value={mainTraining?.id}
                  options={mainTrainingOptions}
                  onChange={(e) => {
                    handleMainTrainingOption(e);
                  }}
                />
              </FormGroup>
            </div>

            <div className="col-lg-4 col-md-6 col-sm-12">
              <FormGroup className="mb-3">
                <Label className="form-label">Core/Speciality</Label>
                <InputField
                  type="select"
                  name="course_type"
                  placeholder=""
                  options={coreAdvanceOptions}
                  onChange={(e) => {
                    handleCoreAdvance(e);
                  }}
                />
              </FormGroup>
            </div>

            <div className="col-lg-4 col-md-6 col-sm-12">
              <FormGroup className="mb-3">
                <Label className="form-label required-field">Form</Label>
                <InputField
                  type="select"
                  name="setup_logbook_master_id"
                  placeholder=""
                  value={selectedForm?.id}
                  options={formOptions}
                  onChange={(e) => {
                    handleFormSelect(e);
                  }}
                  invalid={{ errors, touched }}
                />
              </FormGroup>
            </div>

            <div className="col-lg-4 col-md-6 col-sm-12">
              <FormGroup className="mb-3">
                <Label className="form-label">System Type</Label>
                <InputField
                  type="select"
                  name="case_type"
                  placeholder="Select system type"
                  value={selectedSystemType}
                  options={systemTypeOptions}
                  onChange={handleSystemTypeSelect}
                />
              </FormGroup>
            </div>
            <div className="col-lg-4 col-md-6 col-sm-12">
              <Label>Minimum Target</Label>
              <InputField
                type="number"
                placeholder="Enter Target"
                min='0'
                invalid={{ errors, touched }}
                {...getFieldProps('min_value')}
              />
            </div>
          </div>
          <Button color='warning' type="submit" text="Save" className='px-5' />
        </Form>
      </div>
    </>
  );
};